/**
 * UserSubscriptionPage
 * [UC-046] Subscription via Stripe
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import api from '../core/api-client';
import toast from 'react-hot-toast';
import { fmt, fmtMoney } from '../utils/format';

interface SubscriptionDetails {
  stripeSubscriptionId: string;
  status: string;
  amount: number;
  billingCycle: string;
  pendingBillingCycle?: string | null;
  pendingEffectiveAt?: string | null;
  cancelAtPeriodEnd?: boolean;
  endDate?: string | null;
}

interface PublicPricing {
  monthlyPlanPrice: number;
  yearlyPlanPrice: number;
}

// Render a price like the original hardcoded "€29" / "€279.50":
// drop decimals if the value is a whole number, otherwise show two.
const formatPrice = (n: number) =>
  fmtMoney(n, Number.isInteger(n) ? 0 : 2);

type SubscriptionStatus = 'loading' | 'selecting' | 'success' | 'failed' | 'cancelled';

export function UserSubscriptionPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>('loading');
  const [activeSubscription, setActiveSubscription] = useState<SubscriptionDetails | null>(null);
  const [failureMessage, setFailureMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [justActivated, setJustActivated] = useState(false);
  // Plan prices come from the admin-configured SaaSPricingConfig via the
  // public endpoint, so admin edits propagate to user-facing cards without
  // a redeploy. Null until first fetch resolves.
  const [pricing, setPricing] = useState<PublicPricing | null>(null);

  // Plan-change confirmation modal. Populated from POST /subscriptions/me/preview
  // so the user sees the actual prorated charge (upgrade) or effective date
  // (downgrade) before committing.
  interface PreviewLine { description: string; amount: number; proration: boolean }
  interface PlanPreview {
    kind: 'immediate' | 'scheduled';
    billingCycle: string;
    amount: number;
    amountDueNow: number;
    currency: string;
    effectiveAt: number | null;
    lineItems: PreviewLine[];
  }
  const [preview, setPreview] = useState<PlanPreview | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  // Cancel-subscription modal. `showCancelModal=true` opens the confirm
  // dialog; `isCancelling` is the in-flight POST /subscriptions/me/cancel.
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Load existing subscription on mount
  useEffect(() => {
    checkExistingSubscription();
  }, []);

  // Fetch admin-configured plan prices on mount. Independent of the
  // subscription check so a slow pricing call doesn't block rendering of
  // the current-plan card.
  useEffect(() => {
    api.get<PublicPricing>('/public/saas-pricing')
      .then((p) => setPricing({
        monthlyPlanPrice: Number(p.monthlyPlanPrice) || 0,
        yearlyPlanPrice: Number(p.yearlyPlanPrice) || 0,
      }))
      .catch((err) => {
        console.error('Failed to load public pricing:', err);
      });
  }, []);

  const checkExistingSubscription = async () => {
    try {
      const response = await api.get('/subscriptions/me') as { items?: SubscriptionDetails[] };
      const subscriptions = response.items || [];
      // Endpoint always returns 1 row; detect an active sub via status
      const active = subscriptions.find((s) => s.status === 'Active');

      if (active) {
        setActiveSubscription(active);
        setSubscriptionStatus('success');
      } else {
        setSubscriptionStatus('selecting');
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscriptionStatus('selecting');
    }
  };

  const goBack = () => {
    setSelectedPlan(null);
  };

  // First step of the plan-change flow for existing subscribers: fetch the
  // Stripe proration preview and open the confirm modal. First-time
  // subscribers still go straight to Stripe Checkout (no preview — no
  // existing subscription to prorate against).
  const proceedToStripe = async () => {
    if (!selectedPlan) return;
    const billingCycle = selectedPlan === 'yearly' ? 'Yearly' : 'Monthly';

    if (!activeSubscription) {
      // First-time subscriber: unchanged Stripe Checkout redirect.
      setIsProcessing(true);
      try {
        const response = await api.post('/subscriptions/checkout', { billingCycle }) as { checkoutUrl: string };
        window.location.href = response.checkoutUrl;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        setFailureMessage(t('userSubscription.checkoutError'));
        setSubscriptionStatus('failed');
        toast.error(t('userSubscription.checkoutError'));
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // Existing subscriber: preview first, confirm, then PATCH.
    setIsPreviewLoading(true);
    try {
      const p = await api.post('/subscriptions/me/preview', { billingCycle }) as PlanPreview;
      setPreview(p);
    } catch (error) {
      console.error('Error fetching plan preview:', error);
      toast.error(t('userSubscription.previewError', 'Could not load plan change preview'));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Cancel at period end. User keeps access until their renewal date, no
  // refund. Any pending downgrade is released server-side.
  const confirmCancel = async () => {
    setIsCancelling(true);
    try {
      const result = await api.post('/subscriptions/me/cancel', {}) as {
        cancelAtPeriodEnd: boolean;
        effectiveAt?: number | null;
        billingCycle: string;
      };
      // Refresh to pick up cancel_at_period_end + cleared pending fields.
      const response = await api.get('/subscriptions/me') as { items?: SubscriptionDetails[] };
      const active = (response.items || []).find((s) => s.status === 'Active');
      if (active) setActiveSubscription(active);

      const date = result.effectiveAt
        ? new Date(result.effectiveAt * 1000).toLocaleDateString()
        : '';
      toast.success(
        t('userSubscription.cancelScheduled', {
          defaultValue: 'Subscription ends on {{date}}',
          date,
        }),
        { duration: 6000 },
      );
      setShowCancelModal(false);
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(t('userSubscription.cancelError', 'Failed to cancel subscription'));
    } finally {
      setIsCancelling(false);
    }
  };

  // Fired when the user hits Confirm in the preview modal.
  const confirmPlanChange = async () => {
    if (!preview) return;
    setIsProcessing(true);
    try {
      const result = await api.patch('/subscriptions/me', { billingCycle: preview.billingCycle }) as {
        kind: 'immediate' | 'scheduled';
        billingCycle: string;
        effectiveAt?: number | null;
        subscription: SubscriptionDetails;
      };
      setActiveSubscription(result.subscription);
      setSelectedPlan(null);
      setPreview(null);

      if (result.kind === 'scheduled' && result.effectiveAt) {
        const effectiveDate = new Date(result.effectiveAt * 1000).toLocaleDateString();
        toast.success(
          t('userSubscription.planScheduled', {
            defaultValue: 'Active until {{date}}, then {{cycle}} starts',
            date: effectiveDate,
            cycle: result.billingCycle,
          }),
          { duration: 6000 },
        );
      } else {
        toast.success(t('userSubscription.planChanged', 'Plan updated'));
      }

      // Re-read in case the webhook reconciled anything.
      try {
        const response = await api.get('/subscriptions/me') as { items?: SubscriptionDetails[] };
        const active = (response.items || []).find((s) => s.status === 'Active');
        if (active) setActiveSubscription(active);
      } catch { /* ignore — PATCH response is already authoritative */ }
    } catch (error) {
      console.error('Error changing plan:', error);
      toast.error(t('userSubscription.checkoutError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const retrySubscription = () => {
    setSubscriptionStatus('selecting');
    setSelectedPlan(null);
    setFailureMessage('');
  };

  // Handle URL parameters for success/cancel states
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');

    if (status === 'success') {
      setSubscriptionStatus('success');
      setJustActivated(true);
      toast.success(t('userSubscription.subscriptionActivated'));
      // Stripe redirect may beat the webhook that activates the subscription
      // in the DB. Poll briefly so the success card populates when ready.
      let attempts = 0;
      const maxAttempts = 10;
      const poll = async () => {
        attempts += 1;
        try {
          const response = await api.get('/subscriptions/me') as { items?: SubscriptionDetails[] };
          const subs = response.items || [];
          const active = subs.find((s) => s.status === 'Active');
          if (active) {
            setActiveSubscription(active);
            return;
          }
        } catch { /* ignore transient errors and retry */ }
        if (attempts < maxAttempts) setTimeout(poll, 1000);
      };
      poll();
    } else if (status === 'cancel') {
      setSubscriptionStatus('cancelled');
    }
  }, []);

  if (subscriptionStatus === 'loading') {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
        <div className="mb-5 sm:mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('userSubscription.title')}</h1>
          <p className="mt-1.5 text-base sm:text-sm text-gray-600">{t('userSubscription.subtitle')}</p>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">{t('userSubscription.title')}</h1>
        <p className="mt-1.5 text-base sm:text-sm text-gray-600">{t('userSubscription.subtitle')}</p>
      </div>

      {/* Just-returned-from-Stripe banner. Only while justActivated=true. */}
      {justActivated && (
        <div className="bg-white rounded-lg border border-green-200 p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('userSubscription.subscriptionActivated')}</h2>
              {!activeSubscription && (
                <div className="flex items-center gap-2 mt-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                  <span className="text-sm text-gray-600">Activating your subscription…</span>
                </div>
              )}
              {activeSubscription && (
                <p className="text-sm text-gray-600 mt-1">
                  {activeSubscription.billingCycle} · {fmtMoney(activeSubscription.amount, 2)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Current subscription status card. Rendered whenever we have an
          active subscription — separate from the just-activated banner so
          pending-downgrade and cancel-at-period-end info is visible on every
          page load, not only right after the Stripe redirect. */}
      {activeSubscription && subscriptionStatus !== 'failed' && subscriptionStatus !== 'cancelled' && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {t('userSubscription.currentPlan', 'Current plan')}: {activeSubscription.billingCycle}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {fmtMoney(activeSubscription.amount, 2)} / {activeSubscription.billingCycle.toLowerCase()}
          </p>

          {/* Pending downgrade notice — icon + two-line structure so the
              effective date reads as a heading, the detail as body copy.
              Dark-mode palette: translucent amber background with light
              amber text so the copy stays legible against the dark shell. */}
          {activeSubscription.pendingBillingCycle && activeSubscription.pendingEffectiveAt && (
            <div className="mb-3 flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-700/40">
              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                  {t('userSubscription.pendingDowngradeHeading', {
                    defaultValue: 'Switching to {{cycle}} on {{date}}',
                    cycle: activeSubscription.pendingBillingCycle,
                    date: new Date(activeSubscription.pendingEffectiveAt).toLocaleDateString(),
                  })}
                </div>
                <div className="text-sm text-amber-800 dark:text-amber-200 mt-0.5">
                  {t('userSubscription.pendingDowngradeDetail', {
                    defaultValue: "You'll keep your {{currentCycle}} benefits until then, with no charge today.",
                    currentCycle: activeSubscription.billingCycle,
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Cancel-at-period-end notice — matching shape, red palette.
              Same dark-mode treatment as the amber notice above. */}
          {activeSubscription.cancelAtPeriodEnd && (
            <div className="mb-3 flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-700/40">
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-red-900 dark:text-red-100">
                  {activeSubscription.endDate
                    ? t('userSubscription.cancelHeading', {
                        defaultValue: 'Your subscription ends on {{date}}',
                        date: new Date(activeSubscription.endDate).toLocaleDateString(),
                      })
                    : t('userSubscription.cancelHeadingNoDate', {
                        defaultValue: 'Your subscription will end at the next renewal',
                      })}
                </div>
                <div className="text-sm text-red-800 dark:text-red-200 mt-0.5">
                  {t('userSubscription.cancelDetail', {
                    defaultValue: "You'll keep full access until then. No further charges will be made.",
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Cancel Subscription CTA — only show if not already cancelling */}
          {!activeSubscription.cancelAtPeriodEnd && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowCancelModal(true)}
                className="text-sm font-medium text-red-600 hover:text-red-700 underline"
              >
                {t('userSubscription.cancelSubscription', 'Cancel subscription')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Failure State */}
      {subscriptionStatus === 'failed' && (
        <div className="bg-white rounded-lg border border-red-200 p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('userSubscription.subscriptionFailed')}</h2>
          </div>
          <p className="text-base sm:text-sm text-gray-600 mb-5">{failureMessage || t('userSubscription.defaultFailureMessage')}</p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button onClick={retrySubscription} type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base sm:text-sm font-medium transition-colors">
              {t('userSubscription.retrySubscription')}
            </button>
          </div>
        </div>
      )}

      {/* Cancellation State */}
      {subscriptionStatus === 'cancelled' && (
        <div className="bg-white rounded-lg border border-amber-200 p-5 sm:p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('userSubscription.subscriptionCancelled')}</h2>
          </div>
          <p className="text-base sm:text-sm text-gray-600 mb-5">{t('userSubscription.cancellationMessage')}</p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button onClick={() => setSubscriptionStatus('selecting')} type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base sm:text-sm font-medium transition-colors">
              {t('userSubscription.tryAgain')}
            </button>
          </div>
        </div>
      )}

      {/* Plan cards — always visible (unless failed/cancelled). Current plan
          is marked with an "Active" badge; selecting the OTHER plan lets the
          user switch. */}
      {subscriptionStatus !== 'failed' && subscriptionStatus !== 'cancelled' && (() => {
        const activeCycle = activeSubscription?.billingCycle?.toLowerCase();
        const isMonthlyActive = activeCycle === 'monthly';
        const isYearlyActive = activeCycle === 'yearly';
        // Scheduled-downgrade context: which card represents the INCOMING
        // plan, and what's the effective date. Used to render a persistent
        // "Scheduled from <date>" badge on the target card so the transition
        // is visible every time the user opens the page (not just in the
        // amber banner above).
        const pendingCycle = activeSubscription?.pendingBillingCycle?.toLowerCase();
        const pendingDate = activeSubscription?.pendingEffectiveAt
          ? new Date(activeSubscription.pendingEffectiveAt).toLocaleDateString()
          : '';
        const isMonthlyPending = pendingCycle === 'monthly' && !isMonthlyActive;
        const isYearlyPending = pendingCycle === 'yearly' && !isYearlyActive;
        return (
        <div className="space-y-6">
          {/* Subscription Plans */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Monthly Plan */}
            <div onClick={() => !isMonthlyActive && setSelectedPlan('monthly')}
              className={`bg-white rounded-lg border p-5 sm:p-6 transition-all relative ${
                isMonthlyActive ? 'cursor-default' : 'cursor-pointer'
              } ${
                selectedPlan === 'monthly' && !isMonthlyActive
                  ? 'border-blue-600 ring-2 ring-blue-600 ring-opacity-20'
                  : isMonthlyActive
                    ? 'border-green-500 ring-2 ring-green-500 ring-opacity-20'
                    : 'border-gray-200 hover:border-gray-300'
              }`}>
              {isMonthlyActive ? (
                <div className="absolute -top-3 right-4">
                  <span className="px-3 py-1 text-xs font-semibold bg-green-600 text-white rounded-full">
                    {isYearlyPending && pendingDate
                      ? t('userSubscription.activeUntilBadge', { defaultValue: 'Active until {{date}}', date: pendingDate })
                      : t('userSubscription.activeBadge', { defaultValue: 'Active' })}
                  </span>
                </div>
              ) : isMonthlyPending && pendingDate ? (
                <div className="absolute -top-3 right-4">
                  <span className="px-3 py-1 text-xs font-semibold bg-amber-500 text-white rounded-full">
                    {t('userSubscription.scheduledFromBadge', { defaultValue: 'Scheduled from {{date}}', date: pendingDate })}
                  </span>
                </div>
              ) : null}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('userSubscription.monthlyPlan')}</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  (selectedPlan === 'monthly' || isMonthlyActive)
                    ? 'border-blue-600 bg-blue-600'
                    : 'border-gray-300'
                }`}>
                  {(selectedPlan === 'monthly' || isMonthlyActive) && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
              </div>
              <div className="mb-4">
                <div className="text-3xl font-bold text-gray-900">
                  {pricing ? formatPrice(pricing.monthlyPlanPrice) : '—'}
                  <span className="text-base font-normal text-gray-600">/{t('userSubscription.perMonthShort', 'month')}</span>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('userSubscription.allPremiumFeatures')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('userSubscription.prioritySupport')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('userSubscription.cancelAnytime')}
                </li>
              </ul>
            </div>

            {/* Yearly Plan */}
            <div onClick={() => !isYearlyActive && setSelectedPlan('yearly')}
              className={`bg-white rounded-lg border p-5 sm:p-6 transition-all relative ${
                isYearlyActive ? 'cursor-default' : 'cursor-pointer'
              } ${
                selectedPlan === 'yearly' && !isYearlyActive
                  ? 'border-blue-600 ring-2 ring-blue-600 ring-opacity-20'
                  : isYearlyActive
                    ? 'border-green-500 ring-2 ring-green-500 ring-opacity-20'
                    : 'border-gray-200 hover:border-gray-300'
              }`}>
              {isYearlyActive ? (
                <div className="absolute -top-3 right-4">
                  <span className="px-3 py-1 text-xs font-semibold bg-green-600 text-white rounded-full">
                    {isMonthlyPending && pendingDate
                      ? t('userSubscription.activeUntilBadge', { defaultValue: 'Active until {{date}}', date: pendingDate })
                      : t('userSubscription.activeBadge', { defaultValue: 'Active' })}
                  </span>
                </div>
              ) : isYearlyPending && pendingDate ? (
                <div className="absolute -top-3 right-4">
                  <span className="px-3 py-1 text-xs font-semibold bg-amber-500 text-white rounded-full">
                    {t('userSubscription.scheduledFromBadge', { defaultValue: 'Scheduled from {{date}}', date: pendingDate })}
                  </span>
                </div>
              ) : (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-full">
                    {t('userSubscription.save20Percent')}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">{t('userSubscription.yearlyPlan')}</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  (selectedPlan === 'yearly' || isYearlyActive)
                    ? 'border-blue-600 bg-blue-600'
                    : 'border-gray-300'
                }`}>
                  {(selectedPlan === 'yearly' || isYearlyActive) && <div className="w-2 h-2 bg-white rounded-full"></div>}
                </div>
              </div>
              <div className="mb-4">
                <div className="text-3xl font-bold text-gray-900">
                  {pricing ? formatPrice(pricing.yearlyPlanPrice) : '—'}
                  <span className="text-base font-normal text-gray-600">/{t('userSubscription.perYearShort', 'year')}</span>
                </div>
                {pricing && pricing.yearlyPlanPrice > 0 && (
                  <div className="text-sm text-gray-500">
                    {fmtMoney(pricing.yearlyPlanPrice / 12, 2)}/{t('userSubscription.perMonthShort', 'month')}
                  </div>
                )}
              </div>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('userSubscription.allPremiumFeatures')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('userSubscription.prioritySupport')}
                </li>
                <li className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {t('userSubscription.twoMonthsFree')}
                </li>
              </ul>
            </div>
          </div>

          {/* Action Buttons — shown when a plan is selected that isn't the
              currently active one. If user already has an active plan, the
              primary CTA text becomes "Change Subscription". */}
          {selectedPlan && (
            (selectedPlan === 'monthly' && isMonthlyActive) ||
            (selectedPlan === 'yearly' && isYearlyActive)
              ? null
              : (
            <div className="bg-white rounded-lg border border-gray-200 p-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('userSubscription.selectedPlan')}: {selectedPlan === 'monthly' ? t('userSubscription.monthly') : t('userSubscription.yearly')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {pricing ? (
                      selectedPlan === 'monthly'
                        ? `${formatPrice(pricing.monthlyPlanPrice)}/${t('userSubscription.perMonthShort', 'month')}`
                        : `${formatPrice(pricing.yearlyPlanPrice)}/${t('userSubscription.perYearShort', 'year')} (${fmtMoney(pricing.yearlyPlanPrice / 12, 2)}/${t('userSubscription.perMonthShort', 'month')})`
                    ) : '—'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <button onClick={goBack} type="button" disabled={isProcessing || isPreviewLoading}
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 border border-gray-200 text-base sm:text-sm font-medium transition-colors disabled:opacity-50">
                  {t('userSubscription.back')}
                </button>
                <button onClick={proceedToStripe} type="button" disabled={isProcessing || isPreviewLoading}
                  className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {isPreviewLoading
                    ? t('userSubscription.loadingPreview', 'Loading preview…')
                    : isProcessing
                      ? t('userSubscription.processing')
                      : (activeSubscription ? t('userSubscription.reviewChange', 'Review change') : t('userSubscription.continueToStripe'))}
                </button>
              </div>
            </div>
              )
          )}
        </div>
        );
      })()}

      {/* Cancel-subscription confirmation modal. Shows the effective end
          date (= subscription's current period end — user keeps access
          until then, no refund). Also releases any pending downgrade
          server-side so the queue doesn't fire after cancellation. */}
      {showCancelModal && activeSubscription && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => !isCancelling && setShowCancelModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              {t('userSubscription.cancelConfirmTitle', 'Cancel subscription?')}
            </h2>
            <p className="text-sm text-gray-700 mb-3">
              {t('userSubscription.cancelConfirmBody', {
                defaultValue: 'Your {{cycle}} plan will remain active until its current renewal date. After that you will lose premium access and no further charges will be made. No refund is issued for unused time.',
                cycle: activeSubscription.billingCycle,
              })}
            </p>
            {activeSubscription.pendingBillingCycle && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                {t('userSubscription.cancelOverridePending', {
                  defaultValue: 'Cancelling will remove the scheduled downgrade to {{cycle}}.',
                  cycle: activeSubscription.pendingBillingCycle,
                })}
              </p>
            )}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
                className="w-full sm:w-auto px-5 py-2.5 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 border border-gray-200 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {t('userSubscription.keepSubscription', 'Keep subscription')}
              </button>
              <button
                type="button"
                onClick={confirmCancel}
                disabled={isCancelling}
                className="w-full sm:w-auto px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCancelling
                  ? t('userSubscription.processing')
                  : t('userSubscription.cancelConfirm', 'Cancel at period end')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan-change confirmation modal. Rendered whenever `preview` is set
          by proceedToStripe. Confirm fires PATCH /subscriptions/me; cancel
          clears the preview without touching Stripe. */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => !isProcessing && setPreview(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {preview.kind === 'scheduled'
                ? t('userSubscription.scheduleChangeTitle', 'Schedule plan change')
                : t('userSubscription.confirmChangeTitle', 'Confirm plan change')}
            </h2>

            {preview.kind === 'immediate' ? (
              <>
                <p className="text-sm text-gray-700 mb-3">
                  {t('userSubscription.confirmChangeBody', {
                    defaultValue: 'You are switching to the {{cycle}} plan. Your saved card will be charged the prorated amount below.',
                    cycle: preview.billingCycle,
                  })}
                </p>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 mb-4">
                  {preview.lineItems.length === 0 && (
                    <div className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-gray-600">{preview.billingCycle}</span>
                      <span className="font-medium text-gray-900">
                        {fmtMoney(preview.amountDueNow, 2)}
                      </span>
                    </div>
                  )}
                  {preview.lineItems.map((li, idx) => (
                    <div key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="text-gray-600 break-words flex-1 mr-3">{li.description}</span>
                      <span className={`font-medium ${li.amount < 0 ? 'text-green-700' : 'text-gray-900'}`}>
                        {li.amount < 0 ? '-' : ''}{fmtMoney(Math.abs(li.amount), 2)}
                      </span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-3 py-2 bg-gray-50 text-sm font-semibold">
                    <span className="text-gray-900">
                      {t('userSubscription.dueNow', 'Due now')}
                    </span>
                    <span className="text-gray-900">
                      {fmtMoney(preview.amountDueNow, 2)}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-3">
                  {preview.effectiveAt
                    ? t('userSubscription.scheduleChangeBody', {
                        defaultValue: 'Your current plan continues until {{date}}. On {{date}}, you will switch to the {{cycle}} plan and your saved card will be charged {{amount}}.',
                        date: new Date(preview.effectiveAt * 1000).toLocaleDateString(),
                        cycle: preview.billingCycle,
                        amount: fmtMoney(preview.amount, 2),
                      })
                    : t('userSubscription.scheduleChangeBodyNoDate', {
                        defaultValue: 'Your current plan continues until its next renewal. At that point you will switch to the {{cycle}} plan.',
                        cycle: preview.billingCycle,
                      })}
                </p>
                <div className="border border-gray-200 rounded-lg px-3 py-2 mb-4 text-sm bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">
                      {t('userSubscription.dueNow', 'Due now')}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {fmtMoney(0, 2)}
                    </span>
                  </div>
                </div>
              </>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={() => setPreview(null)}
                disabled={isProcessing}
                className="w-full sm:w-auto px-5 py-2.5 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 border border-gray-200 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {t('userSubscription.back')}
              </button>
              <button
                type="button"
                onClick={confirmPlanChange}
                disabled={isProcessing}
                className="w-full sm:w-auto px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing
                  ? t('userSubscription.processing')
                  : preview.kind === 'scheduled'
                    ? t('userSubscription.scheduleConfirm', 'Schedule change')
                    : t('userSubscription.chargeAndSwitch', 'Charge & switch')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserSubscriptionPage;
